import { useState, useEffect } from 'react';
import { ideasApi, bomApi } from '../lib/api';
import BomTable from '../components/bom/BomTable';
import CostSummary from '../components/bom/CostSummary';

export default function BomBuilder() {
  const [ideas, setIdeas] = useState([]);
  const [selectedIdeaId, setSelectedIdeaId] = useState(null);
  const [step, setStep] = useState(1); // 1=select idea + input, 2=review, 3=saved
  const [inputText, setInputText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Current BOM being worked on
  const [currentBom, setCurrentBom] = useState(null);
  const [extraData, setExtraData] = useState(null); // packaging, assembly, assumptions

  // Saved BOMs list
  const [savedBoms, setSavedBoms] = useState([]);
  const [viewingBom, setViewingBom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedIdeaId) loadBoms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdeaId]);

  const loadIdeas = async () => {
    try {
      const data = await ideasApi.list();
      setIdeas((data.ideas || []).filter(i => i.status !== 'archived'));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const loadBoms = async () => {
    try {
      const data = await bomApi.listForIdea(selectedIdeaId);
      setSavedBoms(data.boms || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerate = async () => {
    if (!selectedIdeaId || !inputText.trim()) {
      setError('Select an idea and describe the product');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const data = await bomApi.generate(selectedIdeaId, inputText);
      setCurrentBom(data.bom);
      setExtraData({ packaging: data.packaging, assembly_cost: data.assembly_cost, assumptions: data.assumptions });
      setStep(2);
    } catch (err) {
      setError(err.message);
    }
    setGenerating(false);
  };

  const handleUpdateComponents = async (newComponents) => {
    if (!currentBom) return;
    try {
      const data = await bomApi.update(currentBom.id, { components: newComponents });
      setCurrentBom(data.bom);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveAndFinish = () => {
    setStep(1);
    setCurrentBom(null);
    setExtraData(null);
    setInputText('');
    loadBoms();
  };

  const handleDeleteBom = async (id) => {
    try {
      await bomApi.delete(id);
      setSavedBoms(prev => prev.filter(b => b.id !== id));
      if (viewingBom?.id === id) setViewingBom(null);
    } catch (err) {
      console.error(err);
    }
  };

  const selectedIdea = ideas.find(i => i.id === selectedIdeaId);

  // Viewing a saved BOM
  if (viewingBom) {
    return (
      <div>
        <button onClick={() => setViewingBom(null)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-vault-navy mb-4 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        <h1 className="text-2xl font-bold text-vault-navy mb-6">BOM Details</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-vault-border p-6">
            <BomTable components={viewingBom.components || []} onUpdate={() => {}} editable={false} />
          </div>
          <div className="bg-white rounded-xl border border-vault-border p-6">
            <CostSummary bom={viewingBom} />
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-vault-navy">BOM Builder</h1>
        <p className="text-gray-500 text-sm mt-1">AI-powered Bill of Materials estimation with cost analysis</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {['Select & Describe', 'Review BOM', 'Done'].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
              step > i + 1 ? 'bg-green-500 text-white' :
              step === i + 1 ? 'bg-vault-gold text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {step > i + 1 ? '\u2713' : i + 1}
            </div>
            <span className={`text-sm ${step === i + 1 ? 'text-vault-navy font-medium' : 'text-gray-400'}`}>{label}</span>
            {i < 2 && <div className="w-8 h-px bg-gray-200" />}
          </div>
        ))}
      </div>

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-vault-error text-sm">{error}</div>}

      {/* Step 1: Input */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">1. Select an Idea</h2>
              <select
                value={selectedIdeaId || ''}
                onChange={e => setSelectedIdeaId(e.target.value || null)}
                className="w-full px-3 py-2 border border-vault-border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-vault-gold"
              >
                <option value="">Choose an idea...</option>
                {ideas.map(idea => (
                  <option key={idea.id} value={idea.id}>{idea.title}</option>
                ))}
              </select>

              {selectedIdea?.description && (
                <div className="mt-3 p-3 bg-vault-surface rounded-lg">
                  <p className="text-sm text-gray-600 line-clamp-3">{selectedIdea.description}</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">2. Describe the Product</h2>
              <textarea
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                placeholder="Describe the physical product in detail: What materials is it made of? How many pieces? Any electronics, moving parts, or special components? What kind of packaging?"
                rows={6}
                className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none"
              />
              <p className="text-xs text-gray-400 mt-2">
                The more detail you provide, the more accurate the BOM estimate will be. Include materials, dimensions, quantity of parts, and packaging type if known.
              </p>

              <button
                onClick={handleGenerate}
                disabled={generating || !selectedIdeaId || !inputText.trim()}
                className="mt-4 w-full py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Generating BOM...
                  </>
                ) : (
                  'Generate BOM Estimate'
                )}
              </button>
            </div>
          </div>

          {/* Saved BOMs sidebar */}
          <div>
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">Saved BOMs</h2>
              {savedBoms.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">
                  {selectedIdeaId ? 'No BOMs yet for this idea' : 'Select an idea to see saved BOMs'}
                </p>
              ) : (
                <div className="space-y-2">
                  {savedBoms.map(bom => (
                    <div key={bom.id} className="p-3 bg-vault-surface rounded-lg group">
                      <div className="flex items-start justify-between">
                        <button onClick={() => setViewingBom(bom)} className="text-left flex-1">
                          <div className="text-sm font-medium text-vault-navy">
                            ${parseFloat(bom.estimated_cost_mid || 0).toFixed(2)} landed
                          </div>
                          <div className="text-xs text-gray-400">{(bom.components || []).length} components</div>
                          <div className="text-xs text-gray-400">{new Date(bom.created_at).toLocaleDateString()}</div>
                        </button>
                        <button onClick={() => handleDeleteBom(bom.id)}
                          className="text-gray-300 hover:text-vault-error opacity-0 group-hover:opacity-100 transition-opacity">
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Review */}
      {step === 2 && currentBom && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">Review & Edit Components</h2>
              <p className="text-sm text-gray-500 mb-4">Edit any line item, add missing components, or remove incorrect ones before saving.</p>
              <BomTable
                components={currentBom.components || []}
                onUpdate={handleUpdateComponents}
                editable={true}
              />
            </div>

            {/* Assumptions */}
            {extraData?.assumptions && (
              <div className="bg-white rounded-xl border border-vault-border p-6">
                <h3 className="text-sm font-semibold text-gray-500 mb-2">Key Assumptions</h3>
                <p className="text-sm text-gray-600">{extraData.assumptions}</p>
              </div>
            )}

            {/* Extra costs */}
            {(extraData?.packaging || extraData?.assembly_cost) && (
              <div className="bg-white rounded-xl border border-vault-border p-6">
                <h3 className="text-sm font-semibold text-gray-500 mb-3">Additional Costs</h3>
                <div className="grid grid-cols-2 gap-4">
                  {extraData.packaging && (
                    <div>
                      <div className="text-xs text-gray-400 uppercase">Packaging ({extraData.packaging.type})</div>
                      <div className="text-lg font-bold text-vault-navy">${(extraData.packaging.estimated_cost_usd || 0).toFixed(2)}</div>
                    </div>
                  )}
                  {extraData.assembly_cost > 0 && (
                    <div>
                      <div className="text-xs text-gray-400 uppercase">Assembly per unit</div>
                      <div className="text-lg font-bold text-vault-navy">${extraData.assembly_cost.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => { setStep(1); setCurrentBom(null); }}
                className="px-4 py-2.5 border border-vault-border text-gray-600 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                Start Over
              </button>
              <button onClick={handleSaveAndFinish}
                className="px-6 py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors">
                Save BOM
              </button>
            </div>
          </div>

          {/* Cost summary sidebar */}
          <div>
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">Cost Summary</h2>
              <CostSummary bom={currentBom} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
